<template>
  <div>
    <Navigation />
    <div class="waiter" v-if="order">
      <div class="left card">
        <div class="menu-head">
          <input v-model="search" placeholder="Search menu…" ref="scan" />
          <select v-model.number="stationFilter">
            <option :value="0">All stations</option>
            <option v-for="s in stations" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="menu-grid">
          <div v-for="m in filteredMenu" :key="m.variant_id" class="menu-tile" @click="addItem(m)">
            <div class="menu-name">{{ m.product_name }}</div>
            <div class="menu-var">{{ m.variant_name }}</div>
            <div class="menu-price">KES {{ format(m.sell_price) }}</div>
            <div class="menu-station" v-if="m.station_name">{{ m.station_name }}</div>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="card order-head">
          <div>
            <h3>{{ headerTitle }} <span class="muted small">· #{{ order.id }}</span></h3>
            <p class="muted small">
              <span v-if="order.tab_type">{{ order.tab_type }} · </span>
              Waiter: {{ order.waiter_name }} · {{ order.guest_count }} guest(s) ·
              <span :class="'o-pill ' + order.status">{{ order.status.replace(/_/g,' ') }}</span>
            </p>
          </div>
          <button class="secondary" @click="goBack">← Back</button>
        </div>
        <div class="card order-lines">
          <h4>Order lines</h4>
          <table v-if="order.items.length">
            <thead><tr><th>Item</th><th style="width:50px">Qty</th><th class="r">Total</th><th style="width:80px">State</th><th></th></tr></thead>
            <tbody>
              <tr v-for="it in order.items" :key="it.id" :class="'row-' + it.state">
                <td>{{ it.product_name }} · {{ it.variant_name_snapshot }}<div class="muted small" v-if="it.station_name">{{ it.station_name }}</div></td>
                <td>{{ Math.round(it.quantity) }}</td>
                <td class="r">KES {{ format(it.line_total) }}</td>
                <td><span :class="'l-pill ' + it.state">{{ it.state }}</span></td>
                <td>
                  <button v-if="it.state === 'draft'" class="danger small" @click="removeItem(it.id)">&times;</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small">Empty — tap menu items to add.</p>
          <div class="totals">
            <div>Draft: <strong>KES {{ format(draftTotal) }}</strong></div>
            <div>Fired: <strong>KES {{ format(firedTotal) }}</strong></div>
            <div class="total">Order total: <strong>KES {{ format(orderTotal) }}</strong></div>
          </div>
        </div>

        <div class="card actions">
          <button class="primary full" :disabled="!hasDrafts || sending" @click="sendToKitchen">
            <i class="mdi mdi-fire"></i>
            {{ sending ? 'Firing…' : 'Send to kitchen (' + draftCount + ' new line' + (draftCount === 1 ? '' : 's') + ')' }}
          </button>
          <button class="secondary full" :disabled="!order.items.length" @click="openCheckout">
            <i class="mdi mdi-cash-register"></i>
            Close table &amp; pay
          </button>
          <p class="err" v-if="err">{{ err }}</p>
          <p class="ok"  v-if="ok">{{ ok }}</p>
        </div>
      </div>
    </div>

    <!-- Checkout modal (with split-bill support) -->
    <div v-if="showCheckout" class="modal-back" @click.self="showCheckout=false">
      <div class="modal card">
        <h3>Close table &amp; take payment</h3>
        <div class="ck-total">Total: <strong>KES {{ format(orderTotal) }}</strong></div>

        <div class="split-tabs">
          <button :class="{'sp-tab':1, active: splitMode === 'single'}" @click="setSplitMode('single')">Single tender</button>
          <button :class="{'sp-tab':1, active: splitMode === 'equal'}"  @click="setSplitMode('equal')">Split equally</button>
          <button :class="{'sp-tab':1, active: splitMode === 'custom'}" @click="setSplitMode('custom')">Split by amount</button>
        </div>

        <div v-if="splitMode === 'equal'" class="equal-controls">
          <label>Number of payers</label>
          <input type="number" min="2" max="20" v-model.number="splitCount" @input="rebuildEqual" style="max-width:120px" />
        </div>

        <div class="splits">
          <div v-for="(sp, i) in splits" :key="i" class="split-row">
            <input v-if="splitMode !== 'single'" v-model="sp.payerName" placeholder="Payer name (optional)" style="flex:1" />
            <select v-model="sp.method" style="max-width:140px">
              <option value="cash">Cash</option>
              <option value="mpesa_till">M-Pesa</option>
              <option value="card">Card</option>
            </select>
            <input type="number" v-model.number="sp.amount" step="0.01" min="0" style="width:100px; text-align:right" />
            <input v-if="sp.method !== 'cash'" v-model="sp.reference" placeholder="Ref" style="width:110px" />
            <button v-if="splitMode === 'custom' && splits.length > 1" class="danger small" @click="splits.splice(i,1)">&times;</button>
          </div>
          <button v-if="splitMode === 'custom'" class="secondary small" @click="addSplit" style="margin-top:8px;">+ Add payer</button>
        </div>

        <div class="split-status" :class="{ ok: splitStatus.matches, warn: !splitStatus.matches }">
          <span>Paid: <strong>KES {{ format(splitStatus.paid) }}</strong></span>
          <span>{{ splitStatus.matches ? '✓ balanced' : (splitStatus.paid < orderTotal ? 'Short KES ' + format(orderTotal - splitStatus.paid) : 'Over KES ' + format(splitStatus.paid - orderTotal)) }}</span>
        </div>

        <div class="modal-actions">
          <button class="secondary" @click="showCheckout=false">Cancel</button>
          <button class="primary" @click="checkout" :disabled="closing || !canClose">{{ closing ? 'Closing…' : 'Confirm payment' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'WaiterPOS',
  components: { Navigation },
  data() {
    return {
      order: null, menu: [], stations: [],
      search: '', stationFilter: 0,
      sending: false, closing: false, err: '', ok: '',
      showCheckout: false,
      splitMode: 'single',
      splitCount: 2,
      splits: [],
    };
  },
  computed: {
    filteredMenu() {
      const q = this.search.trim().toLowerCase();
      let rows = this.menu;
      if (this.stationFilter) rows = rows.filter(m => m.station_id === this.stationFilter);
      if (q) rows = rows.filter(m =>
        (m.product_name || '').toLowerCase().includes(q) ||
        (m.variant_name || '').toLowerCase().includes(q));
      return rows.slice(0, 60);
    },
    headerTitle() {
      const o = this.order;
      if (!o) return '';
      if (o.tab_name) return o.tab_name;
      if (o.table_name) return o.table_name;
      if (o.tab_type === 'takeaway') return 'Takeaway';
      return `Tab #${o.id}`;
    },
    draftTotal() { return this.order ? this.order.items.filter(i => i.state === 'draft').reduce((s, i) => s + Number(i.line_total), 0) : 0; },
    firedTotal() { return this.order ? this.order.items.filter(i => i.state === 'fired').reduce((s, i) => s + Number(i.line_total), 0) : 0; },
    orderTotal() { return this.order ? this.order.items.filter(i => i.state !== 'cancelled').reduce((s, i) => s + Number(i.line_total), 0) : 0; },
    hasDrafts() { return this.order && this.order.items.some(i => i.state === 'draft'); },
    draftCount() { return this.order ? this.order.items.filter(i => i.state === 'draft').length : 0; },
    splitStatus() {
      const paid = this.splits.reduce((s, sp) => s + (Number(sp.amount) || 0), 0);
      return { paid, matches: Math.abs(paid - this.orderTotal) < 0.01 };
    },
    canClose() {
      if (!this.orderTotal || !this.splits.length) return false;
      // Every split needs a positive amount and (for cash) tendered >= amount; for non-cash a reference
      for (const sp of this.splits) {
        if (!(Number(sp.amount) > 0)) return false;
        if (sp.method !== 'cash' && !sp.reference) return false;
      }
      return this.splitStatus.matches || (this.splitMode === 'single' && this.splits[0].method === 'cash' && this.splits[0].amount >= this.orderTotal);
    },
  },
  async mounted() {
    await Promise.all([this.loadOrder(), this.loadMenu(), this.loadStations()]);
  },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    goBack() {
      const back = this.order && this.order.tab_type && this.order.tab_type !== 'table' ? '/tabs' : '/tables';
      this.$router.push(back);
    },
    async loadOrder() {
      const id = this.$route.params.orderId;
      const { data } = await execute({ function: 'getTableOrder', id }, paysol);
      if (data.success) this.order = data.data;
    },
    async loadMenu() {
      const { data } = await execute({ function: 'listMenuItems' }, paysol);
      if (data.success) this.menu = data.data;
    },
    async loadStations() {
      const { data } = await execute({ function: 'listStations' }, paysol);
      if (data.success) this.stations = data.data;
    },
    async addItem(m) {
      const { data } = await execute({ function: 'addOrderItem', orderId: this.order.id, variantId: m.variant_id, quantity: 1 }, paysol);
      if (data.success) await this.loadOrder();
    },
    async removeItem(id) {
      const { data } = await execute({ function: 'removeOrderItem', id }, paysol);
      if (data.success) await this.loadOrder();
      else alert(data.message);
    },
    async sendToKitchen() {
      this.sending = true; this.err = ''; this.ok = '';
      try {
        const { data } = await execute({ function: 'sendOrderToKitchen', orderId: this.order.id }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.ok = data.message; await this.loadOrder();
      } finally { this.sending = false; }
    },
    openCheckout() {
      this.splitMode = 'single';
      this.splits = [{ method: 'cash', amount: this.orderTotal, payerName: '', reference: '' }];
      this.showCheckout = true; this.err = '';
    },
    setSplitMode(m) {
      this.splitMode = m;
      if (m === 'single')      this.splits = [{ method: 'cash', amount: this.orderTotal, payerName: '', reference: '' }];
      else if (m === 'equal')  this.rebuildEqual();
      else if (m === 'custom' && this.splits.length < 2) this.splits.push({ method: 'cash', amount: 0, payerName: '', reference: '' });
    },
    rebuildEqual() {
      const n = Math.max(2, Math.min(20, Number(this.splitCount) || 2));
      const per = Math.round((this.orderTotal / n) * 100) / 100;
      const remainder = Math.round((this.orderTotal - per * n) * 100) / 100;
      const out = [];
      for (let i = 0; i < n; i++) {
        out.push({ method: 'cash', amount: per, payerName: 'Payer ' + (i + 1), reference: '' });
      }
      if (out.length) out[0].amount = Math.round((per + remainder) * 100) / 100;
      this.splits = out;
    },
    addSplit() { this.splits.push({ method: 'cash', amount: 0, payerName: '', reference: '' }); },
    async checkout() {
      this.closing = true; this.err = '';
      const payments = this.splits.map(sp => ({
        method: sp.method,
        amount: Number(sp.amount) || 0,
        payerName: sp.payerName || '',
        reference: sp.method === 'mpesa_till' || sp.method === 'card' ? sp.reference : '',
        mpesaReceipt: sp.method === 'mpesa_till' ? sp.reference : '',
      }));
      try {
        const { data } = await execute({
          function: 'closeTableOrder', orderId: this.order.id,
          payments, discountAmount: 0,
        }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.ok = 'Paid. Receipt: ' + data.data.receiptNo;
        setTimeout(() => this.$router.push('/tables'), 1500);
      } finally { this.closing = false; }
    },
  }
};
</script>

<style scoped>
.waiter { display: grid; grid-template-columns: 1fr 460px; gap: 16px; padding: 16px; height: calc(100vh - 62px); }
.left, .right { display: flex; flex-direction: column; min-height: 0; }
.right { gap: 12px; }
.menu-head { display: flex; gap: 8px; margin-bottom: 12px; }
.menu-head input { flex: 1; }
.menu-head select { max-width: 180px; }
.menu-grid { flex: 1; overflow-y: auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; padding-right: 4px; }
.menu-tile { background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 8px; padding: 12px; cursor: pointer; transition: border-color .15s; }
.menu-tile:hover { border-color: var(--paysol-teal); }
.menu-name { font-weight: 600; font-size: 13px; color: var(--paysol-text); }
.menu-var  { color: var(--paysol-muted); font-size: 11px; margin: 3px 0; }
.menu-price{ color: var(--paysol-teal); font-weight: 700; margin: 4px 0 2px; }
.menu-station { color: var(--paysol-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.order-head { display: flex; justify-content: space-between; align-items: start; }
.order-head h3 { color: var(--paysol-teal); margin: 0 0 3px; font-size: 17px; }
.order-lines { flex: 1; overflow-y: auto; }
.order-lines h4 { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px; }
.totals { border-top: 1px solid var(--paysol-line); margin-top: 10px; padding-top: 10px; display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.totals .total { font-size: 16px; font-weight: 700; color: var(--paysol-teal); margin-top: 4px; padding-top: 6px; border-top: 1px solid var(--paysol-line); }
.actions button { width: 100%; padding: 12px; margin-bottom: 6px; }
.actions button i { margin-right: 6px; }
button.full { width: 100%; }
.o-pill { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.o-pill.open   { background: rgba(139,164,196,.15); color: var(--paysol-muted); }
.o-pill.sent, .o-pill.partially_served { background: rgba(79,195,247,.15); color: #4FC3F7; }
.o-pill.served { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.l-pill { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.l-pill.draft { background: rgba(139,164,196,.15); color: var(--paysol-muted); }
.l-pill.fired { background: rgba(245,158,11,.15); color: var(--paysol-warning); }
.l-pill.cancelled { background: rgba(239,68,68,.15); color: var(--paysol-danger); }
tr.row-draft td:first-child { border-left: 2px solid var(--paysol-muted); }
tr.row-fired td:first-child { border-left: 2px solid var(--paysol-warning); }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.r { text-align: right; }
button.small { padding: 4px 8px; font-size: 11px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok  { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.ck-total { color: var(--paysol-teal); font-size: 22px; text-align: center; padding: 12px 0 16px; }
.change { color: var(--paysol-success); font-weight: 700; margin-top: 6px; }
.split-tabs { display: flex; gap: 4px; background: var(--paysol-bg-elev); padding: 4px; border-radius: 6px; margin-bottom: 14px; }
button.sp-tab { flex: 1; background: transparent; color: var(--paysol-muted); padding: 8px 6px; border-radius: 4px; font-size: 12px; }
button.sp-tab.active { background: var(--paysol-teal); color: #fff; }
.equal-controls { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.equal-controls label { margin: 0; }
.splits { display: flex; flex-direction: column; gap: 8px; }
.split-row { display: flex; gap: 6px; align-items: center; }
.split-status {
  display: flex; justify-content: space-between; gap: 12px;
  margin-top: 12px; padding: 10px 12px; border-radius: 6px;
  font-size: 13px; background: var(--paysol-bg-elev);
}
.split-status.ok    { color: var(--paysol-success); border-left: 3px solid var(--paysol-success); }
.split-status.warn  { color: var(--paysol-warning); border-left: 3px solid var(--paysol-warning); }
button.small { padding: 4px 10px; font-size: 11px; }
</style>
